import { useState, useEffect } from 'react';
import { m, useReducedMotion } from 'framer-motion';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

// Real customers' words, exactly as the website shows them (voolist repo,
// src/data/testimonials.js): never edit a quote, name or role here alone.
const REVIEWS = [
  {
    name: 'Leslea Tash',
    role: 'Full-Time Reseller',
    quote:
      "Voolist has honestly saved me so much time every week. What used to take hours now just takes a few clicks. The support team is also incredible, every time I've had a question, they've responded quickly and really made me feel valued.",
  },
  {
    name: 'Megan Smith',
    role: 'Small Business Owner',
    quote:
      'Voolist helped me increase sales. I was selling only on my Shopify store before, but once I started listing my products on eBay and other platforms, I saw more orders coming in. It is very simple to use and made a huge difference for my business.',
  },
  {
    name: 'Alex Thumm',
    role: 'Full-Time eBay Seller',
    quote:
      "I do like how simple it is to build listings here, and how easy everything looks, and I'm impressed with the communication. You folks have built a wonderful product.",
  },
  {
    name: 'The Relicshop',
    role: 'Full-Time Reseller',
    quote:
      "Voolist is a godsend, thank you. I'm not just saying this, but Voolist has provided me a much needed tool that is very costly most places.",
  },
  {
    name: 'Leslie Morgan',
    role: 'Reseller',
    quote:
      "I spent roughly 5 minutes on Voolist and what a piece of cake! I'm so far thrilled. I cannot begin to tell you how much I love it.",
  },
];

// How long each review stays at the front, in milliseconds
const SHOW_FOR = 6000;
// Cards peeking out under the front one, and by how much
const BEHIND = 2;
const PEEK = 10;

const initials = (name) =>
  name
    .split(' ')
    .filter((word) => word !== 'The')
    .map((word) => word[0])
    .join('');

// ----------------------------------------------------------------------

// The reviews as a stack of cards: one at the front, two peeking out
// underneath. Every few seconds the next one comes forward and the front one
// goes to the back. Hovering pauses it so a review can be read to the end;
// with reduced motion it stays on the first review. All cards share one grid
// cell, so the stack is as tall as the longest review and never jumps.
export default function ReviewStack () {
  const theme = useTheme();

  const reduceMotion = useReducedMotion();

  const [front, setFront] = useState(0);

  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (reduceMotion || paused) return undefined;
    const timer = setInterval(() => setFront((index) => (index + 1) % REVIEWS.length), SHOW_FOR);
    return () => clearInterval(timer);
  }, [reduceMotion, paused]);

  return (
    <Box
      component="section"
      aria-label="What resellers say about Voolist"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      sx={{ display: 'grid', pb: `${BEHIND * PEEK}px` }}
    >
      {REVIEWS.map((review, index) => {
        // 0 is the front card; past BEHIND, cards wait out of sight at the back
        const depth = (index - front + REVIEWS.length) % REVIEWS.length;
        const place = Math.min(depth, BEHIND + 1);

        return (
          <m.figure
            key={review.name}
            aria-hidden={depth !== 0}
            initial={false}
            animate={{ y: place * PEEK, scale: 1 - place * 0.05, opacity: depth <= BEHIND ? 1 : 0 }}
            transition={{ duration: 0.6, ease: [0.32, 0.72, 0, 1] }}
            style={{
              gridArea: '1 / 1',
              zIndex: REVIEWS.length - depth,
              transformOrigin: 'bottom center',
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              padding: theme.spacing(3),
              borderRadius: theme.shape.borderRadius * 2,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
              boxShadow: `0 1px 2px ${alpha(theme.palette.grey[900], 0.04)}, 0 16px 32px -20px ${alpha(theme.palette.grey[900], 0.18)}`,
            }}
          >
            <Iconify icon="ri:double-quotes-l" width={24} sx={{ color: 'primary.main' }} />
            <Typography
              component="blockquote"
              sx={{ m: 0, mt: 1, fontSize: '0.9375rem', lineHeight: 1.6, fontWeight: 500, color: 'text.primary' }}
            >
              {review.quote}
            </Typography>
            {/* Pinned to the bottom, so shorter reviews keep the name in the same place */}
            <Stack component="figcaption" direction="row" alignItems="center" spacing={1.5} sx={{ mt: 'auto', pt: 2 }}>
              <Box
                aria-hidden
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  color: 'primary.darker',
                  bgcolor: alpha(theme.palette.primary.main, 0.16),
                }}
              >
                {initials(review.name)}
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>{review.name}</Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>{review.role}</Typography>
              </Box>
            </Stack>
          </m.figure>
        );
      })}
    </Box>
  );
}
